// SPV Pool v1.0 PR 5 — resolves a single investor's display name from the
// linked Person / Entity. Used by the detail page where fetching exactly one
// counterparty row is cheaper than the whole directory.

import { useQuery } from "@tanstack/react-query";
import { entitiesApi, personsApi } from "@/api/counterparties";
import type { InvestorMaskedDto } from "@/types/investors";

export function useInvestorName(investor: InvestorMaskedDto | undefined) {
  const personId = investor?.personId ?? null;
  const entityId = investor?.entityId ?? null;

  const personQuery = useQuery({
    queryKey: ["counterparties", "person", personId],
    queryFn: () => personsApi.get(personId!),
    enabled: !!personId,
  });
  const entityQuery = useQuery({
    queryKey: ["counterparties", "entity", entityId],
    queryFn: () => entitiesApi.get(entityId!),
    enabled: !!entityId,
  });

  if (personId) {
    return {
      name: personQuery.data?.displayName ?? "Investor",
      counterpartyHref: `/counterparties/persons/${personId}`,
      counterpartyKind: "Person" as const,
      isLoading: personQuery.isLoading,
    };
  }
  if (entityId) {
    return {
      name: entityQuery.data?.legalEntityName ?? "Investor",
      counterpartyHref: `/counterparties/entities/${entityId}`,
      counterpartyKind: "Entity" as const,
      isLoading: entityQuery.isLoading,
    };
  }
  return {
    name: "Investor",
    counterpartyHref: null,
    counterpartyKind: null,
    isLoading: false,
  };
}
